// API van Bestanden delen (/Api/bestanden). Main: Node (backend/routes/bestanden.js), develop:
// engine-voordeur (api/frontdoor/bestanden.py) — zelfde paden, zelfde code hier.
// Verwijderen gebeurt in het scherm zelf (achter bevestig()), zodat het vangnet bij de aanroep staat.
//
// Mappen (okt 2026): een share is een pakket van één of meer bestanden met hun relatieve pad.
// Kiezen kan per bestand, per map (<input webkitdirectory>) of door een map te slepen
// (DataTransferItem.webkitGetAsEntry, recursief). Verborgen bestanden, niet-toegestane typen,
// lege en te grote bestanden worden overgeslagen en gemeld; de server controleert alles opnieuw.
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';

export const BASIS = `${BACKEND_URL}/Api/bestanden`;
// Okt 2026: 25 GB per bestand en 50 GB per pakket (was 2 / 10 GB). "Alles als zip" kan tot 10 GB
// en alleen als elk bestand onder 4 GB blijft; daarboven per bestand downloaden (server: 413).
export const MAX_BYTES = 25 * 1024 ** 3;
export const MAX_PAKKET = 50 * 1024 ** 3;
export const ZIP_MAX = 10 * 1024 ** 3;
export const ZIP_MAX_BESTAND = 4 * 1024 ** 3 - 1;
const GB = (n) => `${Math.round(n / 1024 ** 3)} GB`;
export const GRENS_TEKST = `max. ${GB(MAX_BYTES)} per bestand, ${GB(MAX_PAKKET)} in totaal`;
export const MAX_BESTANDEN = 1000;
export const TOEGESTAAN = ['pdf', 'docx', 'xlsx', 'pptx', 'png', 'jpg', 'jpeg', 'gif', 'mov', 'mp4', 'zip', 'txt', 'md', 'csv'];
export const ACCEPT = TOEGESTAAN.map((e) => `.${e}`).join(',');
const VERBORGEN = new Set(['__macosx', 'thumbs.db', 'desktop.ini', 'ehthumbs.db']);
const MAX_LEZEN = 5000;   // zoveel entries lezen we hooguit uit een gesleepte map

export async function api(pad, { method = 'GET', body, wat = 'Bestanden' } = {}) {
  const r = await authFetch(`${BASIS}${pad}`, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return leesJson(r, wat);
}

const enc = encodeURIComponent;
export const haalLijst = (soort) => api(`?soort=${soort}`, { wat: 'Bestanden laden' });
export const haalCollegas = () => api('/collegas', { wat: "Collega's laden" });
export const haalDetail = (id) => api(`/${enc(id)}`, { wat: 'Bestand openen' });
export const vraagDownload = (id) => api(`/${enc(id)}/download`, { method: 'POST', wat: 'Downloaden' });
export const vraagItemDownload = (id, itemId) => api(`/${enc(id)}/items/${enc(itemId)}/download`, { method: 'POST', wat: 'Downloaden' });
export const vraagZip = (id) => api(`/${enc(id)}/zip`, { method: 'POST', wat: 'Zip downloaden' });
export const bewaar = (id) => api(`/${enc(id)}/bewaren`, { method: 'POST', wat: 'Bewaren' });
/** Versturen. `extern` (optioneel): externe adressen, elk met een persoonlijke downloadlink die
 *  `dagen` (1–90, standaard 14) geldig is. Zonder externen blijft de body zoals hij was. */
export const verstuur = (id, ontvangers, notitie, extern = [], dagen = STANDAARD_DAGEN) => api(`/${enc(id)}/versturen`, {
  method: 'POST',
  body: extern.length ? { ontvangers, notitie, extern, extern_dagen: dagen } : { ontvangers, notitie },
  wat: 'Versturen',
});
export const trekLinkIn = (id, linkId) => api(`/${enc(id)}/extern/${enc(linkId)}/intrekken`, { method: 'POST', wat: 'Intrekken' });
export const herstelLink = (id, linkId) => api(`/${enc(id)}/extern/${enc(linkId)}/herstellen`, { method: 'POST', wat: 'Herstellen' });

// ── externe ontvangers (okt 2026: "ook doorsturen naar externen") ──────────
export const MAX_EXTERN = 10;
export const STANDAARD_DAGEN = 14;
export const MAX_DAGEN = 90;
const ADRES = /^[a-z0-9](?:[a-z0-9._+-]{0,62}[a-z0-9])?@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/;
const INTERN = /@(fullframe\.nl|full-frame\.nl)$/;

/** Controle van één extern adres (de server controleert opnieuw). Geeft { adres, fout }. */
export function controleerExtern(ruw, al = []) {
  const adres = String(ruw || '').trim().toLowerCase();
  if (!adres) return { adres, fout: null };
  if (adres.length > 254 || !ADRES.test(adres)) return { adres, fout: `Geen geldig e-mailadres: ${adres}` };
  if (INTERN.test(adres)) return { adres, fout: `${adres} is een collega; kies die bij 'Aan'` };
  if (al.includes(adres)) return { adres, fout: null };
  if (al.length >= MAX_EXTERN) return { adres, fout: `Maximaal ${MAX_EXTERN} externe ontvangers per keer` };
  return { adres, fout: null };
}

/** Geldigheid in dagen: geheel getal 1–90, anders null. */
export function geldigeDagen(waarde) {
  const n = Number(waarde);
  return Number.isInteger(n) && n >= 1 && n <= MAX_DAGEN ? n : null;
}

/** "geldig tot 3 nov" (NL-tijd). */
export function geldigTot(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return `geldig t/m ${new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'short' }).format(d).replace(/\.$/, '')}`;
}

/** Status van een externe link in Verstuurd als losse delen {tekst, titel}. */
export function externDelen(x, nu = new Date()) {
  const uit = [];
  if (x.status === 'ingetrokken') uit.push({ tekst: 'ingetrokken', titel: `Ingetrokken ${volledigeTijd(x.ingetrokken_at)}` });
  else if (x.status === 'verlopen') uit.push({ tekst: 'verlopen', titel: '' });
  if (x.aantal_downloads > 0) {
    const o = { gedownload_at: x.eerste_download_at, laatst_gedownload_at: x.laatst_gedownload_at, aantal_downloads: x.aantal_downloads };
    uit.push({ tekst: downloadTekst(o, nu), titel: downloadTitel(o) });
  } else {
    uit.push({ tekst: 'nog niet gedownload', titel: '' });
  }
  if (x.status === 'actief') uit.push({ tekst: geldigTot(x.verloopt_at), titel: volledigeTijd(x.verloopt_at) });
  if (x.mail && x.mail !== 'sent') uit.push({ tekst: x.mail === 'dry-run' ? 'mail niet verstuurd (test)' : 'mail niet verstuurd', titel: `Mailstatus: ${x.mail}` });
  return uit;
}

export function extensie(naam) {
  const n = String(naam || '');
  return n.includes('.') ? n.split('.').pop().toLowerCase() : '';
}

export const isVerborgen = (pad) => String(pad || '').split('/')
  .some((d) => d.startsWith('.') || d.startsWith('~$') || VERBORGEN.has(d.toLowerCase()));

/** Lokale controle vóór de upload (de server controleert het nogmaals). Geeft een fouttekst of null. */
export function controleerBestand(bestand) {
  if (!bestand) return 'Kies een bestand';
  if (!TOEGESTAAN.includes(extensie(bestand.name))) return 'Dit bestandstype kan niet: pdf, docx, xlsx, pptx, png, jpg, gif, mov, mp4, zip, txt, md of csv';
  if (!bestand.size) return 'Het bestand is leeg';
  if (bestand.size > MAX_BYTES) return `Het bestand is groter dan ${GB(MAX_BYTES)}`;
  return null;
}

/** Bestanden uit een <input> (los of webkitdirectory): [{ file, pad }]. */
export const uitInvoer = (files) => Array.from(files || [])
  .map((file) => ({ file, pad: file.webkitRelativePath || file.name }));

function leesMap(reader) {
  return new Promise((ok, fout) => reader.readEntries(ok, fout));
}

async function leesEntry(entry, prefix, uit) {
  if (uit.lijst.length + uit.overgeslagen.length > MAX_LEZEN) throw new Error(`Deze map is te groot (meer dan ${MAX_LEZEN} bestanden)`);
  const pad = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile) {
    const file = await new Promise((ok, fout) => entry.file(ok, fout));
    uit.lijst.push({ file, pad });
  } else if (entry.isDirectory) {
    if (isVerborgen(entry.name)) { uit.overgeslagen.push({ pad, reden: 'verborgen map' }); return; }
    const reader = entry.createReader();
    // readEntries geeft per keer een deel (Chrome: 100); doorgaan tot hij leeg terugkomt.
    for (;;) {
      const deel = await leesMap(reader);
      if (!deel.length) break;
      for (const kind of deel) await leesEntry(kind, pad, uit);
    }
  }
}

/** Een drop: mappen recursief, losse bestanden gewoon. Geeft { lijst, overgeslagen }.
 *  Let op: de entries moeten synchroon in de drop-handler worden opgehaald (vóór de eerste await). */
export async function uitDrop(dataTransfer) {
  const entries = Array.from(dataTransfer?.items || [])
    .filter((i) => !i.kind || i.kind === 'file')
    .map((i) => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null))
    .filter(Boolean);
  if (!entries.length) return { lijst: uitInvoer(dataTransfer?.files), overgeslagen: [] };
  const uit = { lijst: [], overgeslagen: [] };
  for (const e of entries) await leesEntry(e, '', uit);
  return uit;
}

/** Filter + grenzen. Geeft { bestanden, overgeslagen, totaal, isMap, naam, fout }. */
export function filterBestanden(lijst, alOvergeslagen = []) {
  const bestanden = [];
  const overgeslagen = [...alOvergeslagen];
  for (const { file, pad: ruw } of lijst || []) {
    const pad = String(ruw || file?.name || '').normalize('NFC');
    const reden = isVerborgen(pad) ? 'verborgen bestand'
      : !TOEGESTAAN.includes(extensie(pad)) ? 'type niet toegestaan'
        : !file?.size ? 'leeg'
          : file.size > MAX_BYTES ? `groter dan ${GB(MAX_BYTES)}` : null;
    if (reden) overgeslagen.push({ pad, reden });
    else bestanden.push({ file, pad });
  }
  const totaal = bestanden.reduce((s, b) => s + b.file.size, 0);
  const isMap = bestanden.length > 1 || bestanden.some((b) => b.pad.includes('/'));
  let fout = null;
  if (!bestanden.length) fout = overgeslagen.length ? `Niets om te delen: ${overgeslagen.length === 1 ? 'het bestand is' : `alle ${overgeslagen.length} bestanden zijn`} overgeslagen` : 'Kies een bestand of map';
  else if (bestanden.length > MAX_BESTANDEN) fout = `Maximaal ${MAX_BESTANDEN} bestanden per keer (dit zijn er ${bestanden.length})`;
  else if (totaal > MAX_PAKKET) fout = `Samen is dit groter dan ${GB(MAX_PAKKET)}; deel het in delen`;
  return { bestanden, overgeslagen, totaal, isMap, naam: standaardNaam(bestanden), fout };
}

/** Pakketnaam zoals de server hem kiest: gemeenschappelijke bovenste map, anders bestandsnaam / 'N bestanden'. */
export function standaardNaam(bestanden) {
  if (!bestanden?.length) return '';
  const top = new Set(bestanden.map((b) => b.pad.split('/')[0]));
  if (top.size === 1 && bestanden.every((b) => b.pad.includes('/'))) return [...top][0];
  if (bestanden.length === 1) return bestanden[0].pad.split('/').pop();
  return `${bestanden.length} bestanden`;
}

// ── upload: blokken met herhaalpogingen, parallel, hervatbaar ─────────────
// Elk blok (8 MB) mag opnieuw: staged blocks met hetzelfde id overschrijven elkaar, en 'klaar' is
// idempotent. Per blok POGINGEN pogingen met oplopende wachttijd (samen ±1 minuut), zodat een
// haperende wifi of een herstartende server een upload van 10 GB niet meer laat mislukken. Lukt
// het dan nog niet, dan onthoudt `staat` welke blokken al binnen zijn: opnieuw Versturen gaat
// verder waar hij was, in hetzelfde concept (de server ruimt concepten pas na 24 uur op).
export const POGINGEN = 5;
export const PARALLEL = 3;           // blokken tegelijk: vult de lijn zonder de server te belasten (3 × 8 MB)
const WACHT_MS = [1000, 3000, 7000, 15000, 30000];
let wacht = (ms) => new Promise((ok) => setTimeout(ok, ms));
/** Alleen voor tests: wachten overslaan. */
export const zetWacht = (f) => { wacht = f; };

const opnieuwStatus = (s) => s === 408 || s === 429 || s >= 500;

/** Voert `doe` uit tot hij lukt; netwerkfouten en 408/429/5xx krijgen een nieuwe poging. */
export async function metPogingen(doe, { onHapert, pogingen = POGINGEN } = {}) {
  for (let poging = 1; ; poging += 1) {
    let r;
    try {
      r = await doe();
    } catch (fout) {
      if (fout?.name === 'AbortError' || fout?.status || poging >= pogingen) throw fout;   // geen netwerkfout: niet herhalen
      onHapert?.(poging);
      await wacht(WACHT_MS[poging - 1] ?? 30000);
      continue;
    }
    if (!r || r.ok || !opnieuwStatus(r.status) || poging >= pogingen) return r;
    onHapert?.(poging);
    await wacht(WACHT_MS[poging - 1] ?? 30000);
  }
}

async function uploadBlokken(url, file, blok, aantal, klaarBlokken, { onBlok, onHapert }) {
  const open = [];
  for (let nr = 0; nr < aantal; nr += 1) if (!klaarBlokken.has(nr)) open.push(nr);
  let fout = null;
  const werker = async () => {
    while (open.length && !fout) {
      const nr = open.shift();
      const stuk = file.slice(nr * blok, Math.min(file.size, (nr + 1) * blok));
      try {
        const r = await metPogingen(() => authFetch(`${url}/blokken/${nr}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: stuk }), { onHapert });
        if (!r.ok) await leesJson(r, 'Uploaden');   // gooit met de melding van de server
        klaarBlokken.add(nr);
        onBlok(stuk.size);
      } catch (e) { fout = fout || e; }
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, open.length) }, werker));
  if (fout) throw fout;
}

/** Nog te gaan in seconden bij `bytes` van `totaal` na `ms` (null zolang er te weinig gemeten is). */
export function resterendeSeconden(bytes, totaal, ms) {
  if (!bytes || ms < 3000 || bytes >= totaal) return null;
  return Math.round(((totaal - bytes) / bytes) * (ms / 1000));
}

/** "nog ±12 min", "nog ±1 u 20 min", "nog een paar seconden". */
export function restTekst(sec) {
  if (sec === null || sec === undefined || !Number.isFinite(sec)) return '';
  if (sec < 45) return 'nog een paar seconden';
  const min = Math.round(sec / 60);
  if (min < 60) return `nog ±${Math.max(1, min)} min`;
  const u = Math.floor(min / 60);
  const m = min % 60;
  return m ? `nog ±${u} u ${m} min` : `nog ±${u} u`;
}

/** Nieuwe uploadstaat voor uploadPakket (bewaar hem bij de keuze, zodat een herhaling hervat). */
export const nieuweUploadStaat = () => ({ start: null, klaar: new Set(), blokken: {} });

/** Upload een pakket (ook een los bestand = pakket van één) in blokken via de server.
 *  onVoortgang({ bestanden, totaalBestanden, bytes, totaalBytes, procent, rest, hapert }).
 *  `staat` (nieuweUploadStaat) maakt de upload hervatbaar. Geeft { id, overgeslagen }. */
export async function uploadPakket({ bestanden, naam }, onVoortgang, staat = nieuweUploadStaat()) {
  if (!staat.start) {
    staat.start = await api('', {
      method: 'POST', wat: 'Upload starten',
      body: { naam: naam || undefined, bestanden: bestanden.map((b) => ({ pad: b.pad, grootte: b.file.size })) },
    });
  }
  const { start } = staat;
  const totaalBytes = start.grootte;
  const totaalBestanden = start.items.length;
  const blokBytes = (item, nr) => Math.min(start.blokgrootte, bestanden[item.index].file.size - nr * start.blokgrootte);
  let bytes = 0;
  for (const item of start.items) {
    if (staat.klaar.has(item.id)) bytes += bestanden[item.index].file.size;
    else for (const nr of staat.blokken[item.id] || []) bytes += blokBytes(item, nr);
  }
  const begin = { t: Date.now(), bytes };
  let hapert = false;
  const meld = () => {
    const ms = Date.now() - begin.t;
    onVoortgang?.({
      bestanden: staat.klaar.size, totaalBestanden, bytes, totaalBytes, hapert,
      procent: totaalBytes ? Math.min(100, Math.floor((bytes / totaalBytes) * 100)) : 0,
      rest: resterendeSeconden(bytes - begin.bytes, totaalBytes - begin.bytes, ms),
    });
  };
  const onHapert = () => { hapert = true; meld(); };
  const onBlok = (n) => { bytes += n; hapert = false; meld(); };
  meld();
  for (const item of start.items) {
    if (staat.klaar.has(item.id)) continue;
    const { file } = bestanden[item.index];
    const url = `${BASIS}/${start.id}/items/${item.id}`;
    staat.blokken[item.id] = staat.blokken[item.id] || new Set();
    await uploadBlokken(url, file, start.blokgrootte, item.blokken, staat.blokken[item.id], { onBlok, onHapert });
    const r = await metPogingen(() => authFetch(`${BASIS}/${start.id}/items/${item.id}/klaar`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ blokken: item.blokken }) }), { onHapert });
    try { await leesJson(r, 'Upload afronden'); } catch (e) {
      if (e.status === 422) staat.blokken[item.id] = new Set();   // server gooide de blob weg: dit bestand opnieuw
      throw e;
    }
    staat.klaar.add(item.id);
    hapert = false;
    meld();
  }
  return { id: start.id, overgeslagen: start.overgeslagen || [] };
}

/** Kan dit pakket als één zip? Tot 10 GB en elk bestand onder 4 GB (`zip` van de server, of de items). */
export function zipKan(pakket, items) {
  if (pakket?.zip === false) return false;   // server: groter dan 10 GB of een bestand van ≥ 4 GB
  if (Number(pakket?.grootte) > ZIP_MAX) return false;
  return !(items || []).some((i) => Number(i.grootte) > ZIP_MAX_BESTAND);
}
export const ZIP_TE_GROOT = `Te groot voor één zip (max. ${GB(ZIP_MAX)}, elk bestand onder 4 GB): download de bestanden los`;

/** Start de download via een korte link (SAS of de ondertekende zip-link, Content-Disposition: attachment). */
export function startDownload(url) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** De zip-link is relatief (/Api/bestanden-zip?t=…): hij hoort bij dezelfde backend als de API. */
export const zipUrl = (pad) => `${BACKEND_URL}${pad}`;

/** Boom uit de items van een pakket: { mappen: [{ naam, pad, knoop }], bestanden: [item] }, gesorteerd. */
export function bouwBoom(items) {
  const wortel = { mappen: new Map(), bestanden: [] };
  for (const item of items || []) {
    const delen = String(item.pad || item.naam || '').split('/');
    let knoop = wortel;
    for (const d of delen.slice(0, -1)) {
      if (!knoop.mappen.has(d)) knoop.mappen.set(d, { mappen: new Map(), bestanden: [] });
      knoop = knoop.mappen.get(d);
    }
    knoop.bestanden.push(item);
  }
  const vorm = (k, pad) => ({
    mappen: [...k.mappen.entries()].sort(([a], [b]) => a.localeCompare(b, 'nl'))
      .map(([naam, sub]) => ({ naam, pad: pad ? `${pad}/${naam}` : naam, knoop: vorm(sub, pad ? `${pad}/${naam}` : naam) })),
    bestanden: [...k.bestanden].sort((a, b) => String(a.naam).localeCompare(String(b.naam), 'nl')),
  });
  return vorm(wortel, '');
}

export function telBoom(knoop) {
  return knoop.bestanden.reduce((s, b) => ({ aantal: s.aantal + 1, grootte: s.grootte + (Number(b.grootte) || 0) }),
    knoop.mappen.reduce((s, m) => { const t = telBoom(m.knoop); return { aantal: s.aantal + t.aantal, grootte: s.grootte + t.grootte }; },
      { aantal: 0, grootte: 0 }));
}

export function grootteTekst(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1).replace('.', ',')} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1).replace('.', ',')} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} kB`;
  return `${n} B`;
}

export const aantalTekst = (n) => `${n} ${Number(n) === 1 ? 'bestand' : 'bestanden'}`;

export function verlooptTekst(dagen) {
  if (dagen === null || dagen === undefined) return '';
  if (dagen <= 0) return 'verloopt vandaag';
  if (dagen === 1) return 'verloopt morgen';
  return `verloopt over ${dagen} dagen`;
}

export const voornaam = (p) => (p?.naam || p?.email || '').split(/[\s@]/)[0];

// ── tijdstippen (okt 2026: "tijdstippen van doorsturen en downloaden") ─────
// Altijd Nederlandse tijd (Europe/Amsterdam), ook als de browser elders staat. Vandaag/gisteren
// relatief ("vandaag 14:32"), anders "8 okt 14:32"; een ander jaar krijgt het jaartal erbij.
const TZ = 'Europe/Amsterdam';
const DELEN = new Intl.DateTimeFormat('nl-NL', {
  timeZone: TZ, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
const DAG = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
const VOLUIT = new Intl.DateTimeFormat('nl-NL', { timeZone: TZ, dateStyle: 'full', timeStyle: 'short' });

function geldig(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Kalenderdag in NL-tijd als dagnummer (voor vandaag/gisteren, ook rond de zomertijdwissel). */
function dagNummer(d) {
  const [j, m, dg] = DAG.format(d).split('-').map(Number);
  return Math.round(Date.UTC(j, m - 1, dg) / 86400000);
}

/** "vandaag 14:32", "gisteren 09:05", "8 okt 14:32" of "3 dec 2025 10:00" (NL-tijd). */
export function tijdTekst(iso, nu = new Date()) {
  const d = geldig(iso);
  if (!d) return '';
  const p = {};
  DELEN.formatToParts(d).forEach((x) => { p[x.type] = x.value; });
  const tijd = `${p.hour}:${p.minute}`;
  const verschil = dagNummer(nu) - dagNummer(d);
  if (verschil === 0) return `vandaag ${tijd}`;
  if (verschil === 1) return `gisteren ${tijd}`;
  const maand = String(p.month || '').replace(/\.$/, '');
  const jaar = DAG.format(nu).slice(0, 4) === p.year ? '' : ` ${p.year}`;
  return `${p.day} ${maand}${jaar} ${tijd}`;
}

/** Voor de tooltip: "donderdag 8 oktober 2026 om 14:32". */
export function volledigeTijd(iso) {
  const d = geldig(iso);
  return d ? VOLUIT.format(d) : '';
}

const aantalDownloads = (o) => Number(o?.aantal_downloads) || (o?.gedownload_at ? 1 : 0);
/** Laatste download; ongeldig of leeg → de eerste. */
const laatste = (o) => (geldig(o?.laatst_gedownload_at) ? o.laatst_gedownload_at : o?.gedownload_at);

/** Downloads van één ontvanger: "gedownload 8 okt 15:02" of "3× gedownload, laatst 9 okt 09:12". */
export function downloadTekst(o, nu = new Date()) {
  const aantal = aantalDownloads(o);
  if (!aantal || !geldig(o?.gedownload_at)) return '';
  if (aantal === 1) return `gedownload ${tijdTekst(o.gedownload_at, nu)}`;
  return `${aantal}× gedownload, laatst ${tijdTekst(laatste(o), nu)}`;
}

/** Tooltip bij de downloads: het eerste (en laatste) moment voluit. */
export function downloadTitel(o) {
  if (!geldig(o?.gedownload_at)) return '';
  if (aantalDownloads(o) > 1) {
    return `Eerste keer ${volledigeTijd(o.gedownload_at)}, laatst ${volledigeTijd(laatste(o))}`;
  }
  return `Gedownload ${volledigeTijd(o.gedownload_at)}`;
}

/** Status van één ontvanger als losse delen {tekst, titel}. Niets gebeurd: "nog niet geopend".
 *  Geopend op hetzelfde moment als de eerste download (meteen gedownload): alleen de download. */
export function ontvangerDelen(o, nu = new Date()) {
  const uit = [];
  const gl = geldig(o?.gelezen_at);
  const gd = geldig(o?.gedownload_at);
  if (gl && !(gd && Math.abs(gl - gd) < 1000)) {
    uit.push({ tekst: `geopend ${tijdTekst(o.gelezen_at, nu)}`, titel: `Geopend ${volledigeTijd(o.gelezen_at)}` });
  }
  const dl = downloadTekst(o, nu);
  if (dl) uit.push({ tekst: dl, titel: downloadTitel(o) });
  if (!uit.length) uit.push({ tekst: 'nog niet geopend', titel: '' });
  return uit;
}
